/**
 * StatusBadge Component
 * Semantic color mapping for status display
 */

import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

type StatusVariant = 'default' | 'secondary' | 'destructive' | 'outline';

const STATUS_CONFIG: Record<string, { label: string; variant: StatusVariant; className?: string }> = {
  // Project statuses
  planning: { label: 'Planejamento', variant: 'secondary', className: 'bg-blue-100 text-blue-800' },
  active: { label: 'Ativo', variant: 'default', className: 'bg-green-100 text-green-800' },
  on_hold: { label: 'Pausado', variant: 'secondary', className: 'bg-yellow-100 text-yellow-800' },
  completed: { label: 'Concluído', variant: 'outline', className: 'bg-card text-foreground' },
  cancelled: { label: 'Cancelado', variant: 'destructive', className: 'bg-red-100 text-red-800' },

  // Appointment statuses
  scheduled: { label: 'Agendado', variant: 'secondary', className: 'bg-blue-100 text-blue-800' },
  confirmed: { label: 'Confirmado', variant: 'default', className: 'bg-green-100 text-green-800' },
  no_show: { label: 'Faltou', variant: 'destructive', className: 'bg-red-100 text-red-800' },

  // Contract statuses
  draft: { label: 'Rascunho', variant: 'secondary' },
  expired: { label: 'Expirado', variant: 'outline' },

  // Work order statuses
  open: { label: 'Aberto', variant: 'default', className: 'bg-blue-100 text-blue-800' },
  in_progress: { label: 'Em Andamento', variant: 'default', className: 'bg-yellow-100 text-yellow-800' },
  waiting_parts: { label: 'Aguardando Peças', variant: 'secondary' },

  // Legal case statuses
  closed_won: { label: 'Ganho', variant: 'default', className: 'bg-green-100 text-green-800' },
  closed_lost: { label: 'Perdido', variant: 'destructive' },
  settled: { label: 'Acordo', variant: 'outline' },

  // Course statuses
  published: { label: 'Publicado', variant: 'default' },
};

interface StatusBadgeProps {
  status: string;
  className?: string;
}

export function StatusBadge({ status, className }: StatusBadgeProps) {
  const config = STATUS_CONFIG[status] || { label: status, variant: 'default' as StatusVariant };

  return (
    <Badge
      variant={config.variant}
      className={cn(config.className, className)}
    >
      {config.label}
    </Badge>
  );
}
